// Date helpers for the route calendar.
//
// The backend `Route.day_of_the_week` is Monday=0 … Sunday=6, and the Nth-of-month
// resolution mirrors the server filter exactly: nth = (day - 1) // 7 + 1.
// JS `Date.getDay()` is Sunday=0 … Saturday=6, so we convert to the Monday-based index.

const pad = n => String(n).padStart(2, "0");

// Monday-based weekday (0=Mon … 6=Sun) matching backend Route.day_of_the_week.
export const weekdayMon0 = date => (date.getDay() + 6) % 7;

// 1st..5th occurrence of this weekday within its month (matches server-side math).
export const nthOfMonth = date => Math.floor((date.getDate() - 1) / 7) + 1;

// Local YYYY-MM-DD (no timezone shift — the backend runs with USE_TZ=False).
export const ymd = date => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const firstOfMonth = date => new Date(date.getFullYear(), date.getMonth(), 1);
export const addMonths = (date, n) => new Date(date.getFullYear(), date.getMonth() + n, 1);
export const isSameMonth = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
export const addDays = (date, n) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + n);
// The Monday on/before the date — the week view's anchor.
export const startOfWeek = date => addDays(date, -weekdayMon0(date));

// Locale for Intl month/weekday names, keyed by the app's language codes.
const INTL_LOCALE = { uk: "uk", en: "en-GB", hy: "hy", ka: "ka" };
export const localeFor = lang => INTL_LOCALE[lang] || "en-GB";

export const monthLabel = (monthDate, lang) => new Intl.DateTimeFormat(localeFor(lang), { month: "long", year: "numeric" }).format(monthDate);

// "31 Aug – 6 Sept 2026": the week view's heading, collapsed by the platform where the parts repeat.
export const weekLabel = (monday, lang) =>
  new Intl.DateTimeFormat(localeFor(lang), { day: "numeric", month: "short", year: "numeric" }).formatRange(monday, addDays(monday, 6));

/**
 * The same label, in the case a preposition demands.
 *
 * Ukrainian `проти` governs the genitive — "проти липня 2026", not "проти липень 2026" — and CLDR
 * only emits the genitive stem when a DAY field is present. So the day is formatted in and then
 * cut back out, along with exactly the one separator that belonged to it (dropping every blank
 * literal would also close up the gap between month and year).
 *
 * Ukrainian ONLY, deliberately. It is the one inflecting language here, and asking Intl for a
 * day-bearing format changes more than the case elsewhere: Armenian returns "հուլիսի, 2026 թ."
 * where the plain label is "2026 թ. հուլիս" — a different word order, not a different ending.
 * Every other locale therefore gets `monthLabel` unchanged.
 */
export const monthLabelGenitive = (monthDate, lang) => {
  if (lang !== "uk") return monthLabel(monthDate, lang);
  const parts = new Intl.DateTimeFormat(localeFor(lang), { day: "numeric", month: "long", year: "numeric" }).formatToParts(monthDate);
  const day = parts.findIndex(part => part.type === "day");
  if (day === -1) return monthLabel(monthDate, lang);
  const neighbour = parts[day + 1]?.type === "literal" ? day + 1 : parts[day - 1]?.type === "literal" ? day - 1 : -1;
  return parts
    .filter((part, index) => index !== day && index !== neighbour)
    .map(part => part.value)
    .join("")
    .trim();
};

// Short weekday headers Mon..Sun in the app language.
export const weekdayHeaders = lang => {
  const fmt = new Intl.DateTimeFormat(localeFor(lang), { weekday: "short" });
  // 2024-01-01 is a Monday.
  return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2024, 0, 1 + i)));
};

export const dayMonthLabel = (date, lang) => new Intl.DateTimeFormat(localeFor(lang), { day: "numeric", month: "short" }).format(date);
